import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import { SERVICES } from "../../lib/constants";


const PROPERTY_SIZES = [
  "Studio",
  "1 Bedroom",
  "2 Bedroom",
  "3 Bedroom",
  "4+ Bedroom",
  "Small Office (< 1000 sqft)",
  "Medium Office (1000–3000 sqft)",
  "Large Office (3000+ sqft)",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  service_type: "",
  location: "",
  property_size: "",
  message: "",
};

export const BookingForm = ({ embedded = false }) => {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

const handleSubmit = (e) => {
  e.preventDefault();

  if (!form.name || !form.phone || !form.service_type || !form.location) {
    toast.error("Please fill in your name, phone, service, and location.");
    return;
  }

  setSubmitting(true);

  const preferredDate = date ? format(date, "yyyy-MM-dd") : "Not selected";

  const message = `
New Cleaning Quote Request

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email || "Not provided"}
Service: ${form.service_type}
Location: ${form.location}
Property Size: ${form.property_size || "Not selected"}
Preferred Date: ${preferredDate}

Message:
${form.message || "No extra message"}
  `;

  const whatsappNumber = "12369946839";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

  window.open(whatsappUrl, "_blank");

  setDone(true);
  setForm(initial);
  setDate(undefined);
  setSubmitting(false);
  toast.success("Opening WhatsApp with your quote request.");
};

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className={cn("py-20 md:py-28 bg-white", embedded && "py-0")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {!embedded && (
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
                Book a Cleaning
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-slate-900">
                Tell us about your space —{" "}
                <span className="font-medium">we'll handle the rest.</span>
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Share a few details and we'll respond with a tailored quote.
                Most requests are answered the same day.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-slate-700">
                {[
                  "Same-day responses",
                  "Transparent, no-obligation quotes",
                  "Flexible weekend & evening slots",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={cn(embedded ? "lg:col-span-12" : "lg:col-span-7")}>
            <div className="relative">
              {/* Outer glow */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-500/20 via-sky-400/15 to-indigo-400/15 blur-3xl rounded-3xl pointer-events-none" />
              <div className="relative bg-white/95 backdrop-blur-xl border border-white/60 rounded-2xl shadow-3d p-6 sm:p-8 lg:p-10 gradient-border">
              {done ? (
                <div
                  data-testid="booking-success"
                  className="py-10 text-center"
                >
                  <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-medium text-slate-900">
                    Request received
                  </h3>
                  <p className="mt-2 text-slate-600">
                    Thank you. A team member will reach out shortly with your
                    tailored quote.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    data-testid="booking-new-btn"
                    className="mt-6 bg-blue-600 hover:bg-blue-700"
                  >
                    Submit another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="booking-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Smith"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                    <Input
                      id="phone"
                      data-testid="booking-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+1 236 994 6839"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700">Service Type *</Label>
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger data-testid="booking-service" className="bg-white">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s.slug} value={s.title}>
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-slate-700">Location *</Label>
                    <Input
                      id="location"
                      data-testid="booking-location"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="Vancouver, Burnaby, Surrey…"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700">Property Size</Label>
                    <Select
                      value={form.property_size}
                      onValueChange={(v) => update("property_size", v)}
                    >
                      <SelectTrigger data-testid="booking-size" className="bg-white">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_SIZES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Preferred Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="booking-date"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-white",
                            !date && "text-slate-400"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email (optional)</Label>
                    <Input
                      id="email"
                      data-testid="booking-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="message" className="text-slate-700">Message</Label>
                    <Textarea
                      id="message"
                      data-testid="booking-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={4}
                      placeholder="Tell us anything special — pets, access details, focus areas…"
                    />
                  </div>

                  <div className="sm:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                    <p className="text-xs text-slate-500">
                      No obligation. We respond within hours.
                    </p>
                    <Button
                      type="submit"
                      data-testid="booking-submit"
                      disabled={submitting}
                      className="w-full sm:w-auto bg-gradient-to-r from-blue-700 via-blue-600 to-sky-500 hover:from-blue-800 hover:to-sky-600 px-7 py-6 text-sm font-semibold shadow-glow-blue"
                    >
                      {submitting ? "Sending…" : "Reserve My Cleaning"}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </form>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
